import { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { CalendarRange, Loader2 } from "lucide-react";
import logger from "../lib/logger";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function MonthlyAttendanceReport({ onDrillDown }) {
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/site-monthly", { params: { year, month } });
      setData(data);
    } catch (err) {
      logger.error("Monthly report failed:", err);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="border border-gray-300 bg-white rounded-sm" data-testid="monthly-report">
      <header className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-gray-200">
        <CalendarRange className="w-5 h-5 text-[#002FA7]" />
        <h3 className="font-black text-lg tracking-tight">Per-site monthly summary</h3>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
        <div className="ml-auto flex items-center gap-2">
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            data-testid="monthly-month-select"
            className="border border-gray-300 px-2 py-1.5 rounded-sm bg-white text-sm"
          >
            {MONTH_NAMES.map((m, i) => (<option key={m} value={i + 1}>{m}</option>))}
          </select>
          <input
            type="number"
            value={year}
            min={2020}
            max={2099}
            onChange={(e) => setYear(Number(e.target.value))}
            data-testid="monthly-year-input"
            className="w-24 border border-gray-300 px-2 py-1.5 rounded-sm bg-white text-sm font-mono"
          />
        </div>
      </header>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 text-[10px] uppercase tracking-widest text-gray-500">
            <tr>
              <th className="text-left px-4 py-2.5">Site</th>
              <th className="text-right px-4 py-2.5">Hours</th>
              <th className="text-right px-4 py-2.5">Sign-ins</th>
              <th className="text-right px-4 py-2.5">Completions</th>
              <th className="text-right px-4 py-2.5">Photos</th>
              <th className="text-right px-4 py-2.5">Overrides</th>
              <th className="text-right px-4 py-2.5">Left fence</th>
              <th className="text-right px-4 py-2.5">No return</th>
            </tr>
          </thead>
          <tbody>
            {!data?.rows?.length && !loading && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400 italic">No data for this month.</td></tr>
            )}
            {data?.rows?.map((r) => (
              <tr
                key={r.site_id}
                className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                data-testid={`monthly-row-${r.site_id}`}
                onClick={() => onDrillDown && onDrillDown(r.site_id, data.year, data.month)}
                title="Open Attendance log filtered for this site"
              >
                <td className="px-4 py-2">
                  <span
                    className="inline-block px-2.5 py-1 rounded-sm border text-xs font-bold"
                    style={r.site_color ? { background: r.site_color.bg, color: r.site_color.text, borderColor: r.site_color.border } : {}}
                  >
                    {r.site_name}
                  </span>
                </td>
                <td className="px-4 py-2 font-mono text-xs text-right font-bold">{r.hours.toFixed(1)}h</td>
                <td className="px-4 py-2 font-mono text-xs text-right">{r.sign_ins}</td>
                <td className="px-4 py-2 font-mono text-xs text-right">{r.completions}</td>
                <td className="px-4 py-2 font-mono text-xs text-right">{r.photos}</td>
                <td className="px-4 py-2 font-mono text-xs text-right">{r.overrides}</td>
                <td className={`px-4 py-2 font-mono text-xs text-right ${r.left_geofence ? "text-red-700 font-bold" : ""}`}>{r.left_geofence}</td>
                <td className={`px-4 py-2 font-mono text-xs text-right ${r.left_without_return ? "text-red-700 font-bold" : ""}`}>{r.left_without_return}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
